import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import * as msgSvc from "../services/messagingService";

const MessagingContext = createContext(null);
const THREAD_POLL_INTERVAL_MS = 3000;
const SUMMARY_POLL_INTERVAL_MS = 15000;

function isDocumentVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

function getActorUnreadField(actor) {
  return actor?.type === "SELLER" ? "seller_unread_count" : "shopper_unread_count";
}

function getConversationUnreadCount(conversation, actor) {
  return Number(conversation?.[getActorUnreadField(actor)] || 0);
}

function patchConversationUnread(conversation, actor, value) {
  return {
    ...conversation,
    [getActorUnreadField(actor)]: value,
  };
}

function sumUnreadConversations(conversationList, actor) {
  return conversationList.reduce(
    (total, conversation) => total + getConversationUnreadCount(conversation, actor),
    0
  );
}

function compareConversationActivity(left, right) {
  const leftTime = new Date(left?.last_message_at || left?.created_at || 0).getTime();
  const rightTime = new Date(right?.last_message_at || right?.created_at || 0).getTime();
  return rightTime - leftTime;
}

function toConversationKey(value) {
  if (value == null) return '';
  return String(value);
}

function toPositiveId(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function getConversationBusinessId(conversation) {
  return toPositiveId(conversation?.business_id ?? conversation?.businessId ?? null);
}

function findConversationById(conversationList, conversationId) {
  const targetKey = toConversationKey(conversationId);
  if (!targetKey) return null;

  return conversationList.find(
    (conversation) => toConversationKey(conversation?.id) === targetKey
  ) || null;
}

function findConversationByBusinessId(conversationList, businessId) {
  const normalizedBusinessId = toPositiveId(businessId);
  if (!normalizedBusinessId) return null;

  return conversationList.find(
    (conversation) => getConversationBusinessId(conversation) === normalizedBusinessId
  ) || null;
}

function getLastMessagePreview(message) {
  return message?.message_text || "[Attachment]";
}

function patchConversationWithLatestMessage(conversation, actor, message) {
  if (!conversation || !message) return conversation;

  return {
    ...conversation,
    last_message_text: getLastMessagePreview(message),
    last_message_at: message.created_at || conversation.last_message_at,
    [getActorUnreadField(actor)]: 0,
  };
}

export function MessagingProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const actor = useMemo(() => {
    if (isAuthenticated) {
      return { type: "SHOPPER", businessId: null };
    }

    return null;
  }, [isAuthenticated]);

  const hasMessagingAccess = Boolean(actor);
  const actorKey = actor ? `${actor.type}:${actor.businessId || "shop"}` : null;

  const [conversations, setConversations] = useState([]);
  const [messagesById, setMessagesById] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [embeddedConversationOpen, setEmbeddedConversationOpen] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [loadedConversationActorKey, setLoadedConversationActorKey] = useState(null);
  const [loadingMessagesById, setLoadingMessagesById] = useState({});
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const actorKeyRef = useRef(null);
  const conversationsRef = useRef([]);
  const refreshPromiseRef = useRef(null);
  const refreshRequestTokenRef = useRef(0);
  const messageRequestMapRef = useRef(new Map());

  const toggleWidget = useCallback(() => setIsWidgetOpen((v) => !v), []);
  const closeWidget = useCallback(() => setIsWidgetOpen(false), []);
  const openWidget = useCallback(() => setIsWidgetOpen(true), []);
  const openEmbeddedConversation = useCallback(() => setEmbeddedConversationOpen(true), []);
  const closeEmbeddedConversation = useCallback(() => setEmbeddedConversationOpen(false), []);
  const clearError = useCallback(() => setError(""), []);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const syncUnreadTotal = useCallback((conversationList) => {
    setUnreadTotal(sumUnreadConversations(conversationList, actor));
  }, [actor]);

  useEffect(() => {
    const nextActorKey = actorKey;
    if (actorKeyRef.current === nextActorKey) return;

    actorKeyRef.current = nextActorKey;
    setConversations([]);
    setMessagesById({});
    setActiveConversationId(null);
    setUnreadTotal(0);
    setEmbeddedConversationOpen(false);
    setLoadingMessagesById({});
    setLoadedConversationActorKey(null);
    setError("");
  }, [actorKey]);

  /* ── Load conversations ────────────────── */
  const refreshConversations = useCallback(async (options = {}) => {
    if (!hasMessagingAccess) return;

    const { silent = false, force = false } = options;
    if (!force && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    if (!silent) {
      setLoadingConvos(true);
    }

    const requestToken = refreshRequestTokenRef.current + 1;
    refreshRequestTokenRef.current = requestToken;

    const requestPromise = (async () => {
      try {
        const list = await msgSvc.listConversations(actor);
        if (refreshRequestTokenRef.current === requestToken) {
          setConversations(list);
          syncUnreadTotal(list);
          if (!silent) {
            setError("");
          }
        }
        return list;
      } catch {
        if (!silent && refreshRequestTokenRef.current === requestToken) {
          setError("Unable to load your conversations right now. Please try again.");
        }
        return [];
      } finally {
        if (!silent) {
          if (refreshRequestTokenRef.current === requestToken) {
            setLoadedConversationActorKey(actorKey);
          }
          setLoadingConvos(false);
        }
        if (refreshPromiseRef.current === requestPromise) {
          refreshPromiseRef.current = null;
        }
      }
    })();

    refreshPromiseRef.current = requestPromise;
    return requestPromise;
  }, [actor, actorKey, hasMessagingAccess, syncUnreadTotal]);

  useEffect(() => {
    if (hasMessagingAccess) {
      refreshConversations();
    } else {
      setConversations([]);
      setMessagesById({});
      setActiveConversationId(null);
      setIsWidgetOpen(false);
      setEmbeddedConversationOpen(false);
      setUnreadTotal(0);
      setLoadingMessagesById({});
      setLoadedConversationActorKey(null);
      setLoadingConvos(false);
    }
  }, [hasMessagingAccess, refreshConversations]);

  /* ── Load messages for active conversation ── */
  const loadMessages = useCallback(async (convId = activeConversationId, options = {}) => {
    const { silent = false } = options;
    if (!convId || !hasMessagingAccess) return [];

    if (messageRequestMapRef.current.has(convId)) {
      return messageRequestMapRef.current.get(convId);
    }

    if (!silent) {
      setLoadingMessagesById((prev) => ({ ...prev, [convId]: true }));
    }
    const requestPromise = (async () => {
      try {
        const msgs = await msgSvc.getMessages(convId, {}, actor);
        const normalizedMessages = Array.isArray(msgs) ? msgs : [];
        setMessagesById((prev) => ({ ...prev, [convId]: normalizedMessages }));
        setError("");
        return normalizedMessages;
      } catch {
        setError("Unable to load this conversation right now. Please try again.");
        return [];
      } finally {
        if (!silent) {
          setLoadingMessagesById((prev) => ({ ...prev, [convId]: false }));
        }
        messageRequestMapRef.current.delete(convId);
      }
    })();

    messageRequestMapRef.current.set(convId, requestPromise);
    return requestPromise;
  }, [activeConversationId, actor, hasMessagingAccess]);

  const markConversationRead = useCallback(async (convId = activeConversationId) => {
    if (!convId || !hasMessagingAccess) return false;

    try {
      await msgSvc.markConversationRead(convId, actor);
      setConversations((prev) => {
        const next = prev.map((conversation) => (
          conversation.id === convId
            ? patchConversationUnread(conversation, actor, 0)
            : conversation
        ));

        syncUnreadTotal(next);
        return next;
      });
      setError("");
      return true;
    } catch {
      setError("Unable to update the read status for this conversation.");
      return false;
    }
  }, [activeConversationId, actor, hasMessagingAccess, syncUnreadTotal]);

  /* ── Poll for new messages on active conversation ── */
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!activeConversationId || (!isWidgetOpen && !embeddedConversationOpen) || !hasMessagingAccess) return;

    const syncActiveConversation = async () => {
      if (!isDocumentVisible()) return;
      await loadMessages(activeConversationId, { silent: true });
      await markConversationRead(activeConversationId);
    };

    syncActiveConversation();

    pollRef.current = setInterval(() => {
      syncActiveConversation();
    }, THREAD_POLL_INTERVAL_MS);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [
    activeConversationId,
    isWidgetOpen,
    embeddedConversationOpen,
    hasMessagingAccess,
    loadMessages,
    markConversationRead,
  ]);

  useEffect(() => {
    if (!hasMessagingAccess) return undefined;

    const refreshVisibleConversations = () => {
      if (!isDocumentVisible()) return;
      refreshConversations({ silent: true });
    };

    const onVisibilityChange = () => {
      if (!isDocumentVisible()) return;
      refreshVisibleConversations();
      if (activeConversationId && (isWidgetOpen || embeddedConversationOpen)) {
        loadMessages(activeConversationId, { silent: true });
        markConversationRead(activeConversationId);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    refreshVisibleConversations();

    const intervalId = window.setInterval(() => {
      refreshVisibleConversations();
    }, SUMMARY_POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [
    activeConversationId,
    embeddedConversationOpen,
    hasMessagingAccess,
    isWidgetOpen,
    loadMessages,
    markConversationRead,
    refreshConversations,
  ]);

  /* ── Select a conversation ── */
  const selectConversation = useCallback(async (id, options = {}) => {
    const { openWidget: shouldOpenWidget = true, openEmbedded = false } = options;

    if (!id) {
      setActiveConversationId(null);
      return [];
    }

    clearError();
    setActiveConversationId(id);
    if (shouldOpenWidget) {
      setIsWidgetOpen(true);
    }
    if (openEmbedded) {
      setEmbeddedConversationOpen(true);
    }

    const [messages] = await Promise.all([
      loadMessages(id),
      markConversationRead(id),
    ]);

    return messages;
  }, [clearError, loadMessages, markConversationRead]);

  const resolveConversationTarget = useCallback(async (target = {}, options = {}) => {
    const { conversationId = null, businessId = null } = target;
    const { openWidget: shouldOpenWidget = true, openEmbedded = false } = options;

    if (actor?.type !== "SHOPPER") return null;

    const normalizedConversationId = toPositiveId(conversationId);
    const normalizedBusinessId = toPositiveId(businessId);

    if (!normalizedConversationId && !normalizedBusinessId) {
      return null;
    }

    clearError();

    if (shouldOpenWidget) {
      setIsWidgetOpen(true);
    }

    if (openEmbedded) {
      setEmbeddedConversationOpen(true);
    }

    let resolvedConversation = normalizedConversationId
      ? findConversationById(conversationsRef.current, normalizedConversationId)
      : null;

    if (!resolvedConversation && normalizedBusinessId) {
      resolvedConversation = findConversationByBusinessId(
        conversationsRef.current,
        normalizedBusinessId
      );
    }

    if (!resolvedConversation && normalizedBusinessId) {
      try {
        const startedConversation = await msgSvc.startConversation(normalizedBusinessId);
        const refreshedConversations = await refreshConversations({
          silent: true,
          force: true,
        });

        resolvedConversation =
          findConversationById(refreshedConversations, startedConversation?.id) ||
          findConversationByBusinessId(refreshedConversations, normalizedBusinessId) ||
          null;
      } catch {
        setError("Unable to start a conversation with this store right now.");
        return null;
      }
    } else if (!resolvedConversation && normalizedConversationId) {
      const refreshedConversations = await refreshConversations({
        silent: true,
        force: true,
      });

      resolvedConversation =
        findConversationById(refreshedConversations, normalizedConversationId) ||
        findConversationByBusinessId(refreshedConversations, normalizedBusinessId) ||
        null;
    }

    const resolvedConversationId =
      toPositiveId(resolvedConversation?.id) || normalizedConversationId;

    if (!resolvedConversationId) {
      setError(
        normalizedBusinessId
          ? "Unable to start a conversation with this store right now."
          : "Unable to open this conversation right now."
      );
      return null;
    }

    await selectConversation(resolvedConversationId, {
      openWidget: shouldOpenWidget,
      openEmbedded,
    });

    return (
      findConversationById(conversationsRef.current, resolvedConversationId) ||
      resolvedConversation ||
      {
        id: resolvedConversationId,
        business_id: normalizedBusinessId,
      }
    );
  }, [actor, clearError, refreshConversations, selectConversation]);

  /* ── Start a conversation with a business ── */
  const startConversation = useCallback(async (businessId, options = {}) => {
    return resolveConversationTarget({ businessId }, options);
  }, [resolveConversationTarget]);

  /* ── Send a message ── */
  const sendMessage = useCallback(async (conversationId, text, file = null) => {
    if (!conversationId) return;
    try {
      const newMsg = await msgSvc.sendMessage(conversationId, text, file, actor);
      if (!newMsg) return null;

      setMessagesById((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), newMsg],
      }));

      setConversations((prev) => {
        const next = prev.map((conversation) => (
          conversation.id === conversationId
            ? patchConversationWithLatestMessage(conversation, actor, newMsg)
            : conversation
        )).sort(compareConversationActivity);

        syncUnreadTotal(next);
        return next;
      });

      await refreshConversations({ silent: true });

      if (activeConversationId === conversationId) {
        await Promise.all([
          loadMessages(conversationId),
          markConversationRead(conversationId),
        ]);
      }

      setError("");
      return newMsg;
    } catch (err) {
      setError(msgSvc.getMessagingErrorMessage(
        err,
        "Unable to send your message right now. Please try again."
      ));
    }
    return null;
  }, [activeConversationId, actor, loadMessages, markConversationRead, refreshConversations, syncUnreadTotal]);

  /* ── Delete a message ── */
  const deleteMessage = useCallback(async (conversationId, messageId) => {
    if (!conversationId || !messageId) return;
    try {
      await msgSvc.deleteMessage(conversationId, messageId, actor);
      setMessagesById((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter((m) => m.id !== messageId),
      }));

      await refreshConversations({ silent: true });

      if (activeConversationId === conversationId) {
        await Promise.all([
          loadMessages(conversationId),
          markConversationRead(conversationId),
        ]);
      }

      setError("");
      return true;
    } catch {
      setError("Unable to delete that message right now.");
    }
    return false;
  }, [activeConversationId, actor, loadMessages, markConversationRead, refreshConversations]);

  const searchConversations = useCallback(async (search) => {
    if (!hasMessagingAccess) return [];

    const normalizedSearch = typeof search === "string" ? search.trim() : "";
    if (!normalizedSearch) return conversations;

    try {
      const results = await msgSvc.listConversations(actor, { q: normalizedSearch });
      setError("");
      return Array.isArray(results) ? results : [];
    } catch {
      setError("Unable to search conversations right now.");
      return [];
    }
  }, [actor, conversations, hasMessagingAccess]);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => toConversationKey(conversation.id) === toConversationKey(activeConversationId)) || null,
    [conversations, activeConversationId]
  );

  const messages = useMemo(
    () => (activeConversationId ? messagesById[activeConversationId] || [] : []),
    [activeConversationId, messagesById]
  );
  const conversationsLoading = loadingConvos || Boolean(
    hasMessagingAccess && loadedConversationActorKey !== actorKey
  );

  const value = useMemo(
    () => ({
      actor,
      conversations,
      activeConversation,
      messagesById,
      messages,
      activeConversationId,
      isWidgetOpen,
      embeddedConversationOpen,
      unreadTotal,
      error,
      loadingConvos: conversationsLoading,
      loadingMessages: Boolean(activeConversationId && loadingMessagesById[activeConversationId]),
      loadingMessagesById,
      toggleWidget,
      closeWidget,
      openWidget,
      openEmbeddedConversation,
      closeEmbeddedConversation,
      clearError,
      selectConversation,
      resolveConversationTarget,
      startConversation,
      sendMessage,
      deleteMessage,
      searchConversations,
      refreshConversations,
      loadMessages,
      markConversationRead,
      getConversationUnreadCount: (conversation) => getConversationUnreadCount(conversation, actor),
    }),
    [
      actor,
      conversations, activeConversation, messagesById, messages, activeConversationId, isWidgetOpen, unreadTotal, error, conversationsLoading,
      embeddedConversationOpen,
      loadingMessagesById,
      toggleWidget,
      closeWidget,
      openWidget,
      openEmbeddedConversation,
      closeEmbeddedConversation,
      clearError,
      selectConversation,
      resolveConversationTarget,
      startConversation,
      sendMessage,
      deleteMessage,
      searchConversations,
      refreshConversations,
      loadMessages,
      markConversationRead,
    ]
  );

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
}

export function useMessaging() {
  const ctx = useContext(MessagingContext);
  if (!ctx) throw new Error("useMessaging must be used inside MessagingProvider");
  return ctx;
}

export default MessagingContext;
